import { getRuntimeSimulationSupersetApiUrl } from '@/runtimeConfig';

// Deep-links into the "Backtest Performance" Superset dashboard
// (keystone-ai/ksai-simulation-service-infra: dashboards/backtest_performance/dashboard.json),
// preselecting its Scenario and Run native filters so the dashboard opens already scoped to
// the run being viewed, instead of whatever was last selected in Superset.
//
// The slug and the two filter ids below are authored in dashboard.json, not generated — that
// is what keeps a link built from them working across dashboard edits (see that file's loader,
// scripts/setup_superset_dashboards.py, upsert_dashboard docstring).
const DASHBOARD_SLUG = 'backtest-performance';
const SCENARIO_FILTER_ID = 'NATIVE_FILTER-scenario';
const RUN_FILTER_ID = 'NATIVE_FILTER-run';
const CANDIDATES_FILTER_ID = 'NATIVE_FILTER-candidates';

// Rison string-quoting for the native_filters URL param. Mirrors setup_superset_dashboards.py's
// own `_rison` helper exactly (same escaping, same "always quote" choice rather than trying to
// detect which strings are bare-safe) — these are opaque ids, not guaranteed rison-bare-safe, so
// quoting unconditionally is the same tradeoff that script already made.
function risonQuote(value: string): string {
  return `'${value.replace(/!/g, '!!').replace(/'/g, "!'")}'`;
}

// One native filter's dataMask, Rison-encoded, `op: IN` against one or more values.
function nativeFilterClause(filterId: string, column: string, values: string[]): string {
  const quoted = `!(${values.map(risonQuote).join(',')})`;
  return (
    `${filterId}:(extraFormData:(filters:!((col:${column},op:IN,val:${quoted}))),` +
    `filterState:(value:${quoted}),id:${filterId},ownState:())`
  );
}

// Builds a link to the Backtest Performance Superset dashboard, filtered to one run and
// optionally its candidates. Returns undefined when this stack has no Superset configured
// (supersetEnabled: false — see ksai-simulation-service-infra's config.py) rather than a link
// that 404s.
//
// experimentIds must be experiment_id (the uuid), not experiment_label: ClickHouse's
// experiment_name column is actually populated from experiment_id — see
// products_inv_simulator's RuntimeSession.sim_output_file_id, whose
// f"{experiment_id}/simulation_output.parquet" is what mds_client.py's list_experiment_files
// parses back into "experiment_name". The label was never involved.
export function buildSupersetRunDeepLink(
  scenarioRunId: string,
  runId: string,
  experimentIds: string[] = [],
): string | undefined {
  const base = getRuntimeSimulationSupersetApiUrl();
  if (!base) return undefined;

  const filters = [
    nativeFilterClause(SCENARIO_FILTER_ID, 'scenario_run_id', [scenarioRunId]),
    nativeFilterClause(RUN_FILTER_ID, 'run_id', [runId]),
  ];
  if (experimentIds.length) {
    filters.push(nativeFilterClause(CANDIDATES_FILTER_ID, 'experiment_name', experimentIds));
  }

  const nativeFilters = `(${filters.join(',')})`;
  return `${base.replace(/\/$/, '')}/superset/dashboard/${DASHBOARD_SLUG}/?native_filters=${encodeURIComponent(nativeFilters)}`;
}
